// Presentation helpers for the wired screens. The API doesn't ship cover art or
// avatar colours, so we derive stable ones from ids/names.
import type { CSSProperties } from 'react';

const AV_COLORS = ['#6b8afd', '#a78bfa', '#fb7185', '#22c55e', '#f59e0b', '#14b8a6', '#ec4899', '#0ea5e9', '#8b5cf6', '#ef4444'];
const GRADS = [
  ['#7c3aed', '#22d3ee'], ['#fb7185', '#f59e0b'], ['#0ea5e9', '#a7f3d0'], ['#f59e0b', '#ef4444'],
  ['#10b981', '#84cc16'], ['#6366f1', '#ec4899'], ['#14b8a6', '#3b82f6'], ['#f43f5e', '#fb923c'],
  ['#8b5cf6', '#22c55e'], ['#64748b', '#94a3b8'],
];

function hash(v: string | number): number {
  const str = String(v);
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Two-letter initials from a display name. "Iain M" → "IM", "Herre" → "HE". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name.trim().slice(0, 2) || '?').toUpperCase();
}

/** Stable avatar colour for a person (seed by user_id, falling back to name). */
export function avatarColor(seed: string | number): string {
  return AV_COLORS[hash(seed) % AV_COLORS.length];
}

/**
 * Inline style for an avatar chip: the person's photo if they have one, else their
 * stable colour (pair with `initials(name)` as the children when there's no photo).
 * `avatar_url` is served on chat/results/boards; roster falls back to colour+initials.
 */
export function avatarStyle(url: string | null | undefined, seed: string | number): CSSProperties {
  return url
    ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: avatarColor(seed) };
}

/** Stable cover gradient for a round/entry. */
export function gradFor(seed: string | number): string {
  const [a, b] = GRADS[hash(seed) % GRADS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

/** The full "you've been invited" message copied by the invite-code buttons. */
export function inviteMessage(code: string): string {
  return `You've been invited to play TPIAP 🎵 — https://www.thisplaceisaprison.co.uk\nYour join code is ${code}`;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th"… */
export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const PACE_LABEL: Record<string, string> = { rapid: 'Rapid', steady: 'Steady', slow: 'Slow' };
/** "rapid" → "Rapid" (null when unset). */
export function paceLabel(p: string | null | undefined): string | null {
  return p ? PACE_LABEL[p] ?? null : null;
}

/** A short "closes in 18h" / "in 3d" / "closing" from an ISO deadline (null if none). */
export function closesIn(deadline: string | null | undefined): string | null {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'closing';
  const mins = ms / 6e4;
  if (mins < 60) return `closes in ${Math.max(1, Math.round(mins))}m`;
  const hours = mins / 60;
  if (hours < 48) return `closes in ${Math.round(hours)}h`;
  return `closes in ${Math.round(hours / 24)}d`;
}
